import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// Languages
import { useTranslation } from "react-i18next";

// CSS
import "./splitpayment.css";

// Components
import CreditCardDetails from "./subComponent/CreditCardDetails/creditcarddetails";

// Services
import { EMPTY_CARD, useCards } from "../../../../contexts/CardsContext";
import TotalPayment from "../TotalPayment";
import { useOrder } from "../../../../contexts/OrderContext";
import { message, notification } from "antd";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import Loading from "react-loading";
import { errorCodes } from "../../PriceOfferComponent/subComponents/PassangerData/passangerdata";

const SplitPayment = ({
  orderReqData,
  isDynamicOrder = false,
  linkedFlightsDealPrice = 0,
  setOrderReqData,
  setDynamicOrder,
  packageId,
  handlingFee,
  baseCommissions,
  cardFee,
  isNewOrder,
  getOrderData,
}) => {
  const { currentHotelOrder } = useOrder();
  const { ldsSupplement } = useSelectedResult();

  // Translator
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cards, updateCards, allCardsInterest } = useCards();
  const [paymentLinks, setPaymentLinks] = useState([]);

  // States

  // const [priceToPay, setPriceToPay] = useState(
  //   parseFloat(currentHotelOrder.ourPrice.priceWithCreditCardFee - sumPaid)
  // );

  const sumPaid = useMemo(() => {
    return cards.reduce((acc, cur) => {
      if (cur.paid) {
        return acc + cur.amountWithInterest;
      } else {
        return acc;
      }
    }, 0);
  }, [cards]);

  const totalPrice = useMemo(() => {
    return parseFloat(
      currentHotelOrder.ourPrice.priceWithCreditCardFee +
        (isDynamicOrder
          ? linkedFlightsDealPrice +
            Object.values(ldsSupplement).reduce(
              (acc, curr) => acc + (curr.checked ? curr.ourFare : 0),
              0
            )
          : 0)
    );
  }, [
    isDynamicOrder,
    currentHotelOrder,
    linkedFlightsDealPrice,
    ldsSupplement,
  ]);

  const priceToPay = useMemo(() => {
    return parseFloat(totalPrice - sumPaid);
  }, [sumPaid, totalPrice]);

  const [paymentLinksTotalToPay, setPaymentLinksTotalToPay] = useState(0);

  useEffect(() => {
    if (orderReqData) {
      if (orderReqData.payments) {
        setPaymentLinks(orderReqData.payments);
        setPaymentLinksTotalToPay(
          orderReqData.payments
            .filter((payment) => payment.paid === true)
            .reduce((acc, cur) => acc + cur.actualAmount, 0)
        );
        const tmpCards = [...cards]; // Create a new array copy
        if (tmpCards.length < orderReqData.payments.length) {
          for (let i = tmpCards.length; i < orderReqData.payments.length; i++) {
            tmpCards.push({ ...EMPTY_CARD });
          }
        }
        for (let i = 0; i < orderReqData.payments.length; i++) {
          tmpCards[i].amount = orderReqData.payments[i].actualAmount;
          tmpCards[i].amountWithInterest = orderReqData.payments[i].amount;
          tmpCards[i].numberofpayments = orderReqData.payments[i].tash;
          tmpCards[i].paid = orderReqData.payments[i].paid;
        }
        updateCards(tmpCards);
      }
      if (
        orderReqData.errors &&
        orderReqData.errors.length > 0 &&
        orderReqData.status !== "OK"
      ) {
        // Collect unique error codes, flattening nested arrays if present
        const flattenedErrors = orderReqData.errors.flat
          ? orderReqData.errors.flat()
          : [].concat(...orderReqData.errors);
        const uniqueCodes = Array.from(
          new Set(
            flattenedErrors.map(
              (error) => error.code || error.ErrorCode || "NO_CODE"
            )
          )
        );
        uniqueCodes.forEach((code) => {
          if (code === "NO_CODE") return;
          const messageText = errorCodes[code];
          if (messageText) {
            notification.error({
              message: "שגיאה בסגירת המלון",
              description: messageText,
              duration: 0,
              placement: "bottomLeft",
            });
          }
        });
        notification.info({
          message: "ישנן שגיאות בסגירת המלון, יש ליצור קשר עם מחלקת אופרציה",
          duration: 0,
          placement: "bottomLeft",
        });
      } else if (orderReqData.paid)
        if (isDynamicOrder) navigate("/dynamic/success/" + orderReqData.id);
        else navigate("/p/dashboard/success/" + orderReqData.id);
    }
  }, [orderReqData]);

  useEffect(() => {
    if (priceToPay <= 0) {
      if (
        orderReqData &&
        (!orderReqData.errors || orderReqData.errors.length === 0)
      ) {
        navigate(
          isDynamicOrder
            ? "/dynamic/success/" + orderReqData.id
            : "/p/dashboard/success/" + orderReqData.id
        );
      }
    }
  }, [priceToPay, orderReqData]);

  const handleCCNumChange = (e) => {
    const newValue = parseInt(e.target.value);

    // Check if the entered value is greater than or equal to 1
    if (!isNaN(newValue) && newValue >= 1) {
      let tempCards = [];
      if (cards.length > newValue) {
        if (paymentLinks.length > newValue)
          return message.error(
            "לא ניתן להפחית את מספר הכרטיסים מתחת לכמות הקישורים לתשלום אשר נוצרו"
          );
        tempCards = cards.slice(0, newValue);
      } else {
        if (paymentLinksTotalToPay < priceToPay) {
          tempCards = [...cards]; // Create a new array copy
          for (let i = cards.length; i < newValue; i++) {
            tempCards.push({ ...EMPTY_CARD });
          }
        } else return message.warning("לא נשאר יותר כסף לתשלום");
      }
      // tempCards[0].amount = currentHotelOrder.ourPrice.priceWithCreditCardFee;

      updateCards(tempCards);
    } else {
      // If the entered value is less than 1 or not a valid number, set it to 1
      updateCards([EMPTY_CARD]);
    }
  };

  return (
    <>
      <div className="splitPayment-wrapper splitPayment-sub-wrapper">
        {/* Set Credit Card Number */}
        <div className="splitPayment-card-amount-wrapper-wrapper">
          <div className="splitPayment-card-amount-wrapper lg:tw-flex lg:tw-w-[90%]">
            <div className="splitPayment-card-amount">
              <div className="splitPayment-card-amount-button">
                <label className="splitPayment-card-amount-title">
                  {t("paymentagent.setnumcard")}
                </label>
                <select
                  name="splitPayment-card-amount-input"
                  id="splitPayment-card-amount-input"
                  value={cards.length}
                  onChange={handleCCNumChange}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>
            </div>
            <div className="flex-column">
              <div className="splitPayment-card-amount-totalprice">
                <label className="splitPayment-card-amount-price-title">
                  {t("paymentagent.priceis")}:
                  <span
                    id="priProblem-total"
                    className="splitPayment-card-amount-price-number"
                  >
                    <TotalPayment totalPrice={totalPrice} />
                  </span>
                </label>
              </div>
              {!isDynamicOrder && isNewOrder && packageId && (
                <button
                  className="passangerData-submit-button-pay-later"
                  onClick={() => {
                    // Navigate to payment page
                    navigate("/p/dashboard/searchoffer/" + packageId);
                  }}
                >
                  {t("paymentagent.backToReserve")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Credit Card Displayer */}
        <div className="splitPayment-card-displayer">
          {!totalPrice ? (
            <div
              className="tw-flex tw-justify-center tw-items-center tw-h-full tw-w-full"
              style={{ minHeight: "500px" }}
            >
              <Loading type="spin" color="#000" className="loading-spinner" />
            </div>
          ) : (
            cards.map((card, idx) => (
              <div key={`card-${idx}-${cards.length}`}>
                <CreditCardDetails
                  cardNumber={idx + 1}
                  cardIndex={idx}
                  orderReqData={orderReqData}
                  setOrderReqData={setOrderReqData}
                  setDynamicOrder={setDynamicOrder}
                  paymentLink={
                    idx < paymentLinks.length ? paymentLinks[idx] : null
                  }
                  handlingFee={handlingFee}
                  baseCommissions={baseCommissions}
                  priceToPay={priceToPay}
                  paymentLinksTotalToPay={paymentLinksTotalToPay}
                  cardFee={cardFee}
                  getOrderData={getOrderData}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default SplitPayment;
